'use client'

// The pictures in his bag's pop-up (components/ui/BagPanel.jsx), and the one in the bubble over his head
// (components/three/npc/ThoughtBubble.jsx).
//
// Drawn the way the vending machine's drop draws (VendingDrop.jsx, its psx material): plain WebGL in a canvas of its
// own, a low buffer shown in big pixels, the corners snapped to a coarse grid, the texture laid on without perspective,
// fifteen-bit colour with an ordered dither. The world's renderer is never touched, so no new material comes into the
// world, and nothing here is drawn while the pop-up is shut and nobody is near him.
//
// One renderer for the lot, made when the pop-up or the bubble first wants a picture and let go when nothing holds it
// any more (holdPictures). Each thing is drawn once, at a three-quarter turn, and kept as an image for the rest of the visit, so opening the bag again draws nothing
// it has drawn before. Only the thing that is picked turns: the same renderer draws it a frame at a time and each frame
// is copied into the one small canvas on the page. The models are read once a visit; each comes in with its own Draco
// decoder and Basis transcoder, the site's, let go once nothing is loading.

import * as THREE from 'three'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js'
import { DRACO_PATH, BASIS_PATH, bufferedKTX2 } from '../three/hooks/useModel'
import { preparePieces } from '../three/world/BoughtThings'
import { BUY_MODEL, partsFor } from '../../lib/items/parts'
import { trinketLook } from '../../lib/world/trinkets'
import { TRINKET_MODEL, VEND_MODEL, lookKey } from '../../lib/things/looks'
import { psx } from './VendingDrop'

// The buffer every picture is drawn at, before it is blown up: the drop draws at 208 for half the screen, and a place
// in the bag is about a sixth of that across. The turning one is the same buffer shown twice the size.
export const PICTURE_PX = 64
// every other pixel of the buffer, as the drop's grid is
const SNAP = new THREE.Vector2(PICTURE_PX / 4, PICTURE_PX / 4)
// how big a thing stands on the stage: its bounding sphere, so a long tray and a tall cup both fit at any turn
const RADIUS = 1.32
// the turn a picture is drawn at: a little from above, a little from the side
const STILL = { x: 0.32, y: -0.62 }

let stage = null
let noGl = false
const pictures = new Map() // key -> data URL, for the visit
const asked = new Map() // key -> Promise of a data URL, while the pop-up is open
const things = new Map() // key -> Promise of an object on the stage, for the visit
const models = new Map() // url -> Promise of a parsed model, for the visit
let loaders = null
let loading = 0

function openStage() {
  if (stage || noGl || typeof document === 'undefined') return stage
  const canvas = document.createElement('canvas')
  try {
    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: false, preserveDrawingBuffer: true, powerPreference: 'low-power' })
    renderer.setPixelRatio(1)
    renderer.setSize(PICTURE_PX, PICTURE_PX, false)
    renderer.setClearColor(0x000000, 0)
    const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 50)
    camera.position.set(0, 0.15, 5.2)
    camera.lookAt(0, 0, 0)
    stage = { renderer, camera, scene: new THREE.Scene(), canvas }
  } catch (err) {
    // no WebGL: the places show their names and nothing breaks
    console.warn('[bag] no WebGL for the pictures', err)
    noGl = true
  }
  return stage
}

// how many want the renderer kept: the pop-up while it is open, the bubble while it draws the newest thing in his bag
let holders = 0

/**
 * Keep the renderer while pictures are wanted. Returns the let-go; when the last holder lets go, the renderer goes and
 * the pictures already drawn stay. Two hold it now, and the pop-up shutting must not take it from the bubble halfway
 * through a picture, or the other way round.
 */
export function holdPictures() {
  holders++
  let held = true
  return () => {
    if (!held) return
    held = false
    if (--holders === 0) releasePictures()
  }
}

// Nothing holds it: the renderer goes, the pictures already drawn stay.
function releasePictures() {
  if (!stage) return
  const { renderer, scene } = stage
  stage = null
  asked.clear()
  scene.clear()
  renderer.dispose()
  renderer.forceContextLoss?.()
}

function model(url) {
  if (!models.has(url)) {
    const s = openStage()
    if (!s) return Promise.resolve(null)
    if (!loaders) {
      const ktx2 = new KTX2Loader().setTranscoderPath(BASIS_PATH).detectSupport(s.renderer)
      const draco = new DRACOLoader().setDecoderPath(DRACO_PATH)
      loaders = { ktx2, draco }
    }
    const { ktx2, draco } = loaders
    const loader = new GLTFLoader().setDRACOLoader(draco).setKTX2Loader(ktx2)
    loader.register(bufferedKTX2)
    loading++
    const p = loader.loadAsync(url).finally(() => {
      // the decoder's and the transcoder's workers go once nothing is loading, as the drop's do
      if (--loading === 0 && loaders) { loaders.ktx2.dispose(); loaders.draco.dispose(); loaders = null }
    })
    p.catch(() => models.delete(url))
    models.set(url, p)
  }
  return models.get(url)
}

// One material a source material (and tint), and one a flat colour, for the visit. Each keeps the material it was
// made from, so a burnt or pale piece can be made again from the original with its tint (lib/items/parts.js DONENESS).
const made = new Map()
function psxOf(src, tint = null) {
  const key = `${src?.uuid || 'none'}:${tint ? tint.join(',') : ''}`
  if (!made.has(key)) {
    const colour = src?.color ? src.color.clone() : new THREE.Color(1, 1, 1)
    if (tint) colour.multiply(new THREE.Color(...tint))
    const m = psx(src?.map || null, colour, undefined, 0, SNAP)
    m.userData.source = src || null
    made.set(key, m)
  }
  return made.get(key)
}
function flat(hex) {
  const key = `flat:${hex}`
  if (!made.has(key)) made.set(key, psx(null, new THREE.Color(hex), undefined, 0, SNAP))
  return made.get(key)
}

// the meal pieces, measured as the world measures them, in this stage's material
let mealPieces = null
function meals() {
  if (!mealPieces) {
    mealPieces = model(BUY_MODEL).then((gltf) => (gltf ? preparePieces(gltf.scene, (m) => psxOf(m)).pieces : null))
    mealPieces.catch(() => { mealPieces = null })
  }
  return mealPieces
}

// A build of a meal, as BoughtThings.jsx builds it for the ground: the same parts list, this stage's material.
async function meal(itemId, spec) {
  const pieces = await meals()
  if (!pieces) return null
  const group = new THREE.Group()
  const size = (k) => pieces.get(k)?.size || { x: 0, y: 0, z: 0 }
  for (const p of partsFor(itemId, spec, size)) {
    let obj = null
    if (p.disc) {
      obj = new THREE.Mesh(new THREE.CircleGeometry(p.disc.radius, 8), flat(p.disc.color))
      obj.rotation.x = -Math.PI / 2
    } else {
      const src = pieces.get(p.piece)
      if (!src) continue
      obj = src.holder.clone(true)
      if (p.tint) {
        obj.traverse((o) => {
          if (!o.isMesh || Array.isArray(o.material)) return
          o.material = psxOf(o.material.userData.source || null, p.tint)
        })
      }
    }
    obj.position.set(p.x || 0, p.y || 0, p.z || 0)
    if (p.ry) obj.rotation.y = p.ry
    if (p.scale) obj.scale.setScalar(p.scale)
    group.add(obj)
  }
  return group.children.length ? group : null
}

async function vended(node) {
  const gltf = await model(VEND_MODEL)
  const src = gltf?.scene.getObjectByName(node)
  if (!src) return null
  const thing = src.clone(true)
  thing.position.set(0, 0, 0)
  thing.traverse((o) => { if (o.isMesh) o.material = psxOf(o.material) })
  return thing
}

async function found(kind, colour) {
  const gltf = await model(TRINKET_MODEL)
  const src = gltf?.scene.getObjectByName(`trinket_${kind}`)
  if (src?.isMesh) return new THREE.Mesh(src.geometry, psxOf(src.material))
  // every kind the town finds is in the model (test/hoard.test.js); one that is not is a ball in its colour
  const look = trinketLook(kind, colour)
  return new THREE.Mesh(new THREE.SphereGeometry(look.size / 2, 10, 8), flat(look.colour))
}

// Centred and brought to the stage's size, under a holder that turns.
function fitted(obj) {
  if (!obj) return null
  const box = new THREE.Box3().setFromObject(obj)
  if (box.isEmpty()) return null
  const sphere = box.getBoundingSphere(new THREE.Sphere())
  obj.position.sub(sphere.center)
  const holder = new THREE.Group()
  holder.add(obj)
  holder.scale.setScalar(RADIUS / (sphere.radius || 1))
  return holder
}

function thingFor(look) {
  const key = lookKey(look)
  if (!things.has(key)) {
    const p = (async () => fitted(
      look.buy ? await meal(look.buy, look.spec || {})
        : look.vend ? await vended(look.vend)
          : look.trinket !== undefined ? await found(look.trinket, look.colour)
            : null,
    ))()
    p.catch(() => things.delete(key))
    things.set(key, p)
  }
  return things.get(key)
}

function draw(s, obj, x, y) {
  obj.rotation.set(x, y, 0)
  s.scene.add(obj)
  s.renderer.render(s.scene, s.camera)
  s.scene.remove(obj)
}

/** False once this browser has turned out to have no WebGL, when the places show names in place of pictures. */
export function canDraw() {
  return !noGl
}

/** A picture already drawn this visit, or null. */
export function pictureNow(look) {
  return pictures.get(lookKey(look)) || null
}

/** The picture of a thing, drawn once a visit: a data URL, or null when it cannot be drawn. */
export function pictureOf(look) {
  const key = lookKey(look)
  if (!key) return Promise.resolve(null)
  if (pictures.has(key)) return Promise.resolve(pictures.get(key))
  if (!openStage()) return Promise.resolve(null)
  if (!asked.has(key)) {
    asked.set(key, thingFor(look).then((obj) => {
      const s = stage
      // (the pop-up may have closed while the model came in: then it is drawn next time)
      if (!obj || !s) return null
      draw(s, obj, STILL.x, STILL.y)
      const url = s.canvas.toDataURL('image/png')
      pictures.set(key, url)
      return url
    }).catch((err) => {
      console.warn('[bag] could not draw', key, err?.message || err)
      return null
    }))
  }
  return asked.get(key)
}

/**
 * The picked thing, turning, in a canvas on the page (PICTURE_PX square). Returns the stop. A visitor who asks for less
 * motion gets it standing still.
 */
export function turn(look, canvas) {
  const ctx = canvas?.getContext('2d')
  if (!ctx) return () => {}
  // the last thing picked goes at once, not when the next one has loaded
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  if (!lookKey(look) || !openStage()) return () => {}
  const still = !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
  let dead = false
  let raf = 0
  let obj = null
  let started = 0
  const frame = (ms) => {
    if (dead) return
    const s = stage
    if (s && obj) {
      started ||= ms
      const t = (ms - started) / 1000
      draw(s, obj, still ? STILL.x : 0.28 + Math.sin(t * 0.9) * 0.1, still ? STILL.y : STILL.y + t * 1.25)
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(s.canvas, 0, 0, canvas.width, canvas.height)
    }
    if (!still) raf = requestAnimationFrame(frame)
  }
  thingFor(look).then((o) => {
    if (dead || !o) return
    obj = o
    raf = requestAnimationFrame(frame)
  }).catch(() => {})
  return () => { dead = true; cancelAnimationFrame(raf) }
}
